import { instituteToday } from '@/features/teacher/utils';
import type { CalendarEventItem } from '../types/calendar';

/**
 * The calendar's date arithmetic, in one place.
 *
 * Every date on this screen is an ISO `YYYY-MM-DD` string, never a `Date` in
 * state. Two reasons: the events arrive from the API as strings, so a grid
 * built from strings needs no conversion in either direction; and comparing
 * strings is comparing days, while comparing `Date`s is comparing instants —
 * which is how "is this event today" starts depending on the time of day.
 *
 * `Date` is used only inside these helpers, and always built from the three
 * parts rather than parsed from the string: `new Date('2026-05-01')` is UTC
 * midnight, which is the previous day for anyone west of Greenwich.
 */

/** A `Date` at local midnight on that day. */
export function fromISO(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
}

export function toISO(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Today at the school, not at the device.
 *
 * It decides the "today" highlight and where "Upcoming events" starts, and a
 * phone with the wrong date or zone moved both. The one definition of the
 * school's day lives with `instituteToday`; this is the calendar's name for it.
 */
export function todayISO(): string {
  return instituteToday();
}

export function addDays(iso: string, count: number): string {
  const date = fromISO(iso);
  date.setDate(date.getDate() + count);
  return toISO(date);
}

/**
 * The same day of a later or earlier month, or the last day of it.
 *
 * Without the clamp, stepping forward from 31 March lands on 1 May, and
 * stepping back again lands on 1 April — the month picker would skip a month
 * on the way out and not come back.
 */
export function addMonths(iso: string, count: number): string {
  const date = fromISO(iso);
  const targetDay = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + count);
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(targetDay, lastDay));
  return toISO(date);
}

export function startOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

/** Sunday, because that is the column the grid opens on. */
export function startOfWeek(iso: string): string {
  const date = fromISO(iso);
  return addDays(iso, -date.getDay());
}

/**
 * The six weeks a month grid draws: the days of the month, plus the tail of the
 * month before and the head of the one after so every row is full.
 *
 * Always 42 cells rather than as many as the month needs, so the panel does not
 * change height as the reader steps through the year.
 */
export function monthCells(year: number, month: number): string[] {
  const first = new Date(year, month, 1);
  const start = addDays(toISO(first), -first.getDay());
  return Array.from({ length: 42 }, (_, index) => addDays(start, index));
}

export function weekCells(iso: string): string[] {
  const start = startOfWeek(iso);
  return Array.from({ length: 7 }, (_, index) => addDays(start, index));
}

/**
 * Every month a session touches, in order.
 *
 * Taken from the session's own ends rather than assumed to be April–March: the
 * academic year is a row in the database with real dates on it, and an institute
 * whose session opens on 31 March would lose a month to a calculation that
 * counted twelve from April (CLAUDE.md §4).
 */
export function monthsBetween(from: string, to: string): { year: number; month: number }[] {
  const months: { year: number; month: number }[] = [];
  const last = fromISO(to);
  const cursor = fromISO(startOfMonth(from));

  while (cursor <= last) {
    months.push({ year: cursor.getFullYear(), month: cursor.getMonth() });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return months;
}

/** Keeps a day inside the session, so no view can wander off what was loaded. */
export function clampDay(iso: string, from: string, to: string): string {
  if (iso < from) return from;
  if (iso > to) return to;
  return iso;
}

/**
 * Whether an entry is on the calendar on that day.
 *
 * A ten-day vacation belongs to all ten days, not only the one it starts on —
 * a reader looking at the 22nd needs to see that the institute is shut.
 */
export function coversDay(item: CalendarEventItem, iso: string): boolean {
  return item.startDate <= iso && item.endDate >= iso;
}

/** Whether any part of an entry falls inside the window, both ends included. */
export function overlapsRange(item: CalendarEventItem, from: string, to: string): boolean {
  return item.startDate <= to && item.endDate >= from;
}
